/**
 * KnowledgeGapsWidget
 *
 * Dashboard homepage widget showing knowledge gaps pending research.
 * Displays count badge and up to 5 items sorted by priority.
 * Links to Domain Knowledge Dashboard for research.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 19, 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BookOpen,
  Search,
  ArrowRight,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface KnowledgeGap {
  id: string;
  knowledgeType: string;
  title: string;
  researchStatus: string;
  status: string;
  createdAt: string;
}

const typeLabels: Record<string, string> = {
  terminology: 'Terminology',
  specification: 'Specification',
  context: 'Context',
  expertise_signal: 'Expertise',
  validation_rule: 'Validation'
};

export function KnowledgeGapsWidget() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [gaps, setGaps] = useState<KnowledgeGap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchGaps();
    }
  }, [selectedSite?.id]);

  const fetchGaps = async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/domain-knowledge?siteId=${selectedSite.id}&researchStatus=pending`);
      const data = await response.json();
      if (data.success && data.data) {
        // API returns paginated response with items array
        setGaps(data.data.items || []);
      } else {
        setError('Failed to load knowledge gaps');
      }
    } catch (err) {
      console.error('[KnowledgeGapsWidget] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (gap: KnowledgeGap) => {
    // Navigate to Domain Knowledge Dashboard with the gap in context
    navigate(`/domain-knowledge?gapId=${gap.id}`);
  };

  const handleViewAll = () => {
    navigate('/domain-knowledge?tab=gaps');
  };

  if (!selectedSite?.id) {
    return null;
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Knowledge Gaps
          </CardTitle>
          <Badge variant="secondary">Error</Badge>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchGaps}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <BookOpen className="h-4 w-4" />
          Knowledge Gaps
        </CardTitle>
        {loading ? (
          <Skeleton className="h-5 w-8" />
        ) : (
          <Badge variant={gaps.length > 0 ? 'default' : 'secondary'}>
            {gaps.length}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        ) : gaps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500 mb-2" />
            <p className="text-sm text-muted-foreground">
              No knowledge gaps to research!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {gaps.slice(0, 5).map((gap) => (
              <div
                key={gap.id}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer transition-colors"
                onClick={() => handleItemClick(gap)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleItemClick(gap);
                  }
                }}
              >
                <Search className="h-4 w-4 text-yellow-500" />
                <span className="flex-1 text-sm truncate" title={gap.title}>
                  {gap.title}
                </span>
                <Badge variant="outline" className="text-xs">
                  {typeLabels[gap.knowledgeType] || gap.knowledgeType}
                </Badge>
              </div>
            ))}
          </div>
        )}

        <Button
          variant="ghost"
          className="w-full mt-3"
          onClick={handleViewAll}
        >
          View All Gaps
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default KnowledgeGapsWidget;
